import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Target, Calendar, ArrowRight } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mois en français
const MONTHS_FR = {
  '01': 'Janvier', '02': 'Février', '03': 'Mars', '04': 'Avril',
  '05': 'Mai', '06': 'Juin', '07': 'Juillet', '08': 'Août',
  '09': 'Septembre', '10': 'Octobre', '11': 'Novembre', '12': 'Décembre'
};

const formatMonth = (monthStr) => {
  if (!monthStr) return '';
  const [year, month] = monthStr.split('-');
  return `${MONTHS_FR[month]} ${year}`;
};

export default function ObjectivesReminder() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [showReminder, setShowReminder] = useState(false);
  const [currentMonth, setCurrentMonth] = useState('');
  const [monthLabel, setMonthLabel] = useState('');

  useEffect(() => {
    // Ne pas afficher le popup pour les rôles sans objectifs personnels de vente
    // (admin, partenaire, responsable agence — ils supervisent, ne vendent pas eux-mêmes)
    if (['admin', 'partner', 'responsable_agence'].includes(user?.role)) return;
    // Ne pas afficher pour les users sans accès Octopus
    // (objectifs gérés par partenaire, ILEK n'a pas encore son module d'objectifs)
    const slugs = (user?.allowed_partners || []).map(p => p.slug);
    const hasOctopus = !user?.allowed_partners || slugs.includes('octopus');
    if (!hasOctopus) return;
    checkReminder();
  }, [user]);

  const checkReminder = async () => {
    try {
      if (!token) return;
      
      const response = await axios.get(`${API_URL}/objectives/check-reminder`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.should_show_reminder) {
        setCurrentMonth(response.data.current_month);
        setMonthLabel(formatMonth(response.data.current_month));
        setShowReminder(true);
      }
    } catch (error) {
      console.error('Error checking objectives reminder:', error);
    }
  };

  const handleGoToObjectives = () => {
    setShowReminder(false);
    navigate('/mes-objectifs');
  };

  const handleRemindLater = () => {
    setShowReminder(false);
    // Le rappel réapparaîtra à la prochaine connexion car l'objectif n'est pas créé
  };

  return (
    <Dialog open={showReminder} onOpenChange={setShowReminder}>
      <DialogContent className='max-w-md'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2 text-xl'>
            <Target className='h-6 w-6 text-primary' />
            Définissez vos objectifs
          </DialogTitle>
          <DialogDescription className='text-base pt-2'>
            Nouveau mois, nouveaux objectifs !
          </DialogDescription>
        </DialogHeader>
        
        <div className='space-y-4 py-4'>
          <div className='flex items-center gap-3 p-4 bg-primary/10 rounded-lg'>
            <Calendar className='h-10 w-10 text-primary' />
            <div>
              <p className='font-semibold text-lg'>{monthLabel}</p>
              <p className='text-sm text-muted-foreground'>
                Commencez le mois du bon pied !
              </p>
            </div>
          </div>
          
          <p className='text-sm text-muted-foreground'>
            Définissez vos objectifs mensuels pour suivre votre progression :
          </p>
          
          <ul className='text-sm space-y-2 ml-4'>
            <li className='flex items-center gap-2'>
              <span className='w-2 h-2 bg-green-500 rounded-full'></span>
              Nombre de ventes prévues
            </li>
            <li className='flex items-center gap-2'>
              <span className='w-2 h-2 bg-blue-500 rounded-full'></span>
              Pourcentage de Dual cible
            </li>
            <li className='flex items-center gap-2'>
              <span className='w-2 h-2 bg-yellow-500 rounded-full'></span>
              Leads solaires prévus
            </li>
          </ul>
          
          <p className='text-xs text-muted-foreground italic'>
            Ce message continuera d'apparaître jusqu'à ce que vous définissiez vos objectifs.
          </p>
        </div>
        
        <div className='flex gap-2'>
          <Button 
            variant='outline' 
            onClick={handleRemindLater}
            className='flex-1'
          >
            Plus tard
          </Button>
          <Button 
            onClick={handleGoToObjectives}
            className='flex-1'
          >
            Définir maintenant
            <ArrowRight className='h-4 w-4 ml-2' />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
